import { createSelector, createSlice } from "@reduxjs/toolkit";
import { generateClient } from 'aws-amplify/api';
import * as queries from "../graphql/queries";
import * as mutations from "../graphql/mutations";
import { reset } from "./globalActions";
import moment from "moment";

const client = generateClient();

const moduleName = "media";

const defaultImageFilters = {
    customerId: null,
    startDate: moment().add(-2, "year").startOf("day").toISOString(),
    endDate: moment().endOf("day").toISOString(),
};

const initialState = {
    loading: false,
    media: [],
    activeMedia: null,
    nextToken: null,
    previousTokens: [],
    imageFilters: defaultImageFilters,
    imageSearchKey: null,
    imageScrollOffset: 0,
};

const mediaSlice = createSlice({
    name: moduleName,
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, () => initialState);
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setMedia: (state, action) => {
            state.media = action.payload.items;
            state.nextToken = action.payload.nextToken;
        },
        appendMedia: (state, action) => {
            state.media = [...state.media, ...action.payload.items]
            state.nextToken = action.payload.nextToken
        },
        addMedia: (state, action) => {
            state.media.unshift(action.payload);
        },
        setActiveMedia: (state, action) => {
            state.activeMedia = action.payload;
        },
        pushPreviousToken: (state, action) => {
            state.previousTokens.push(action.payload);
        },
        popPreviousToken: (state) => {
            state.previousTokens.pop();
        },
        resetPagination: (state) => {
            state.nextToken = null;
            state.previousTokens = [];
        },

        setImageFilters: (state, action) => {
            state.imageFilters = {
                ...state.imageFilters,
                ...action.payload,
            };
        },

        resetImageBrowser: (state) => {
            state.media = [];
            state.nextToken = null;
            state.previousTokens = [];
            state.imageFilters = { ...defaultImageFilters };
            state.imageSearchKey = null;
            state.imageScrollOffset = 0;
        },

        setImageSearchKey: (state, action) => {
            state.imageSearchKey = action.payload;
        },

        setImageScrollOffset: (state, action) => {
            state.imageScrollOffset = action.payload;
        },
    },
});

export const { setLoading, setMedia, addMedia, appendMedia, setActiveMedia, pushPreviousToken, popPreviousToken, resetPagination, setImageFilters, resetImageBrowser, setImageSearchKey, setImageScrollOffset } = mediaSlice.actions;
export default mediaSlice.reducer;

export const selectState = (state) => state[moduleName];
export const selectLoading = createSelector(selectState, (state) => state.loading);
export const selectMedia = createSelector(selectState, (state) => state.media)
export const selectActiveMedia = createSelector(selectState, (state) => state.activeMedia)
export const selectNextToken = createSelector(selectState, (state) => state.nextToken)
export const selectHasNextPage = createSelector(selectState, state => !!state.nextToken);
export const selectHasPreviousPage = createSelector(selectState, state => state.previousTokens.length > 0);
export const selectPreviousTokens = createSelector(selectState, state => state.previousTokens);
export const selectImageFilters = createSelector(selectState, (state) => state.imageFilters);
export const selectImageSearchKey = createSelector(selectState, (state) => state.imageSearchKey);
export const selectImageScrollOffset = createSelector(selectState, (state) => state.imageScrollOffset);

const fetchPage = async ({ startDate, endDate, customerId, locationId, nextToken, limit = 12 }) => {
    const { data: { mediaByCustomerId } } = await client.graphql({
        query: queries.mediaByCustomerId,
        variables: {
            customerId,
            sortDirection: 'DESC',
            createdAt: {
                between: [startDate, endDate]
            },
            filter: {
                locationId: {
                    eq: locationId
                },
            },
            limit,
            nextToken,
        }
    });

    return mediaByCustomerId;
}

export const fetchCustomerMedia = (startDate, endDate, customerId, locationId, nextToken) => async (dispatch) => {
    const result = await fetchPage({
        startDate,
        endDate,
        customerId,
        locationId,
        nextToken: null,
    });

    if (!nextToken) dispatch(setMedia(result));
    else dispatch(appendMedia(result));
}

export const loadNextPage = ({ startDate, endDate, customerId, locationId, }) => async (dispatch, getState) => {

    const state = getState()[moduleName];

    if (!state.nextToken) return;

    dispatch(pushPreviousToken(state.nextToken));

    const result = await fetchPage({
        startDate,
        endDate,
        customerId,
        locationId,
        nextToken: state.nextToken,
    });

    dispatch(setMedia(result));
}

export const loadPreviousPage = ({ startDate, endDate, customerId, locationId, }) => async (dispatch, getState) => {

    const state = getState()[moduleName];

    if (!state.previousTokens.length) return;

    const previousTokens = [...state.previousTokens];

    // remove current page token
    previousTokens.pop();

    // token to fetch previous page
    const previousToken =
        previousTokens[previousTokens.length - 1] || null;

    dispatch(popPreviousToken());

    const result = await fetchPage({
        startDate,
        endDate,
        customerId,
        locationId,
        nextToken: previousToken,
    });

    dispatch(setMedia(result));
}

export const fetchMedia = (id) => async (dispatch) => {
    const { data: { getMedia } } = await client.graphql({
        query: queries.getMedia,
        variables: {
            id,
        }
    })
    dispatch(setActiveMedia(getMedia));
}

export const createMedia = ({ filename, description, mediaType, sortKey, userId, locationId, customerId }) => async (dispatch) => {
    const { data } = await client.graphql({
        query: mutations.createMedia,
        variables: {
            input: {
                filename,
                description,
                mediaType,
                sortKey,
                userId,
                locationId,
                customerId,
                createdAt: moment().toISOString(),
            }
        }
    })

    dispatch(addMedia(data.createMedia))
}

